import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { useModalState } from 'src/Modal/ModalStateContext'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { DocumentItemMarkdownEditor } from 'src/pages/Policies/PoliciesShowPage/DocumentGrid/DocumentItem/DocumentItemMarkdownEditor'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { PolicyRecord } from '@trustero/trustero-api-web/lib/model/policy_pb'
import {
  PrimaryButton,
  StandardButtonSize,
  StandardButtonVariant,
} from '../../Buttons'
import { RteModalQueryParams } from '../../../RichTextEditor'
import { AddDocumentForm } from '../../../ModalForms/Document'
import { ModalFormId } from '../../../ModalForms'
import { FlexAlign, FlexRow } from '../../Flex'
import { TabPanelNoteItem } from './styles'

type Props = {
  policy: PolicyRecord.AsObject
  name: string
  template: string
  isNotApplicable: boolean
  onUpdate: () => void
}

export const DocumentTabNote = ({
  policy,
  isNotApplicable,
  template,
  onUpdate,
}: Props): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const { openModal } = useModalState()
  const canAddPolicyDocsFromTemplate =
    useCanPerformAction<PolicyRecord.AsObject>(
      CAPABILITY.CAN_ADD_POLICY_DOCS_FROM_TEMPLATE,
      policy,
    )
  const canAddPolicyDocs = useCanPerformAction<PolicyRecord.AsObject>(
    CAPABILITY.CAN_ADD_POLICY_DOCS,
    policy,
  )

  return (
    <TabPanelNoteItem>
      <FlexRow
        gap={10}
        align={FlexAlign.FLEX_START}
        justify={FlexAlign.FLEX_END}
      >
        {template && (
          <>
            <DocumentItemMarkdownEditor
              modelId={policy.modelId}
              title={template.split(/\r?\n/)?.[0]?.replace('# ', '')}
              markdown={template}
              onUpdate={onUpdate}
            />
            <PrimaryButton
              size={StandardButtonSize.SMALL}
              onClick={() => {
                if (isNotApplicable) {
                  openModal(ModalFormId.NA_INFO)
                  return
                }
                navigate(
                  queryString.stringifyUrl(
                    {
                      url: location.pathname,
                      query: {
                        ...queryString.parse(location.search, {
                          arrayFormat: 'bracket',
                        }),
                        [RteModalQueryParams.SHOW_RTE_MODAL]: true,
                        [RteModalQueryParams.UNIQUE_ID]: policy.modelId,
                        [RteModalQueryParams.SHOW_WIZARD]: false,
                      },
                    },
                    {
                      arrayFormat: 'bracket',
                    },
                  ),
                )
              }}
              text="New from Template"
              isDisabled={!canAddPolicyDocsFromTemplate}
              disabledReason="You do not have permission to add a policy document from template."
            />
          </>
        )}
        <StandardOpenModalButton
          modalId={
            isNotApplicable ? ModalFormId.NA_INFO : ModalFormId.ADD_DOCUMENT
          }
          text="Add Documentation"
          variant={StandardButtonVariant.PRIMARY}
          size={StandardButtonSize.SMALL}
          isDisabled={!canAddPolicyDocs}
          disabledReason="You do not have permission to add a policy document."
        />
      </FlexRow>
      <AddDocumentForm policyId={policy.modelId} openOnDrag />
    </TabPanelNoteItem>
  )
}
